import {
  faArrowLeft,
  faArrowRight,
  faCalendar,
  faCalendarPen,
  faComments,
  faEnvelope,
  faFiles,
  faLocationPin,
  faStamp,
  faUsers,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { SimpleGrid } from 'tailwindComponents/grid/SimpleGrid';
import {
  getSigningAppointmentDisplayAddress,
  getSigningAppointmentDisplayDateTime,
  getSigningAppointmentTransactionCategoryDisplayLabel,
  ISigningAppointmentResponse,
} from 'types/signingAppointment/ISigningAppointmentResponse';
import { Show } from 'components/shared/Show';
import { eSigningMethod } from 'types/signingAppointment/eSigningMethod';
import { INotaryDetailsData } from 'types/signingAppointment/INotaryDetailsData';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../types/shared/UUID';
import { useSelectCurrentOrder } from '../../../../selectors/order/selectCurrentOrder';
import { CorrectionsButton } from '../corrections/button/CorrectionsButton';
import { Tooltip } from '../../../../ui-toolkit';
import { AppointmentStatusBadge } from '../Reusable/AppointmentStatusBadge';
import { isNil } from 'lodash';

export interface ISigningAppointmentItemProps {
  appointment: ISigningAppointmentResponse;
  onViewAppointment: (appointmentId?: string) => void;
  appointmentsWithCorrections: string[];
  onAddEventClick: (appointmentId: UUID) => void;
}

const getNotaryDescription = (
  isSignatureSyncAppointment: boolean,
  signingMethod: string,
  notary: INotaryDetailsData
) => {
  if (signingMethod === eSigningMethod.eSign) {
    return 'E-sign Only';
  } else if (signingMethod === eSigningMethod.wetSign) {
    return 'Wet-signing, No Notary Needed';
  } else if (isSignatureSyncAppointment && signingMethod !== eSigningMethod.eSign) {
    return 'Signature Sync';
  } else if (!!notary) {
    return `${notary.firstName} ${notary.lastName}`;
  } else {
    return 'Unassigned';
  }
};

export const AppointmentItem: React.FC<ISigningAppointmentItemProps> = ({
  appointment,
  onViewAppointment,
  appointmentsWithCorrections,
  onAddEventClick,
}) => {
  const { appointmentId } = useParams<{ appointmentId?: UUID }>();
  const { data: order } = useSelectCurrentOrder();
  const isSelected = appointmentId === appointment.id;
  const { signers, notary, notes, isSignatureSyncAppointment, signingMethod } = appointment;

  const onAppointmentClick = () => {
    onViewAppointment(appointment.id);
  };

  const onViewOriginalAppointmentClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    appointmentId?: string
  ) => {
    event.stopPropagation();
    onViewAppointment(appointmentId);
  };

  const onCreateCorrectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    onAddEventClick(appointment.id);
  };

  return (
    <div
      className={`flex cursor-pointer flex-col items-start space-y-2 p-4 text-sm hover:bg-green-50 ${
        isSelected ? 'bg-green-50' : 'bg-white'
      }`}
      onClick={onAppointmentClick}
      data-test-id={`event-summary-${appointment.id}`}
    >
      <SimpleGrid className="w-full" wrap>
        <div className="flex w-full items-baseline justify-between space-x-4">
          <div className="flex-1 overflow-hidden break-words">
            <Show when={!!appointment.isDigitalScheduling}>
              <Tooltip tooltipContent={'Digital Scheduling Appointment'} trigger={'hover'} placement={'top'}>
                <FontAwesomeIcon className="mr-2 mt-1" icon={faEnvelope} />
              </Tooltip>
            </Show>
            <span className="text-content-default-content-text font-inter font-bold capitalize leading-5">
              {getSigningAppointmentTransactionCategoryDisplayLabel(
                appointment,
                order.transaction.transaction_category,
                true // short title flag
              )}
            </span>
            <Show when={appointmentsWithCorrections.includes(appointment.id)}>
              <Tooltip tooltipContent={'Corrections needed'} trigger={'hover'} placement={'top'}>
                <div className="ml-2">
                  <FontAwesomeIcon data-test-id="corrections-icon" className="mr-2 mt-1" icon={faWarning} />
                </div>
              </Tooltip>
            </Show>
            <Show when={!isNil(appointment.correctionTriggeredById)}>
              <span className={'text-sm text-blue-600'}>
                <div onClick={(event) => onViewOriginalAppointmentClick(event, appointment.correctionTriggeredById)}>
                  <FontAwesomeIcon className="mr-2 mt-1" icon={faCalendarPen} />
                  View original appointment
                </div>
              </span>
            </Show>
          </div>

          <div className="flex space-x-2">
            <AppointmentStatusBadge appointment={appointment} />
            <FontAwesomeIcon className="mr-2 mt-1" icon={isSelected ? faArrowLeft : faArrowRight} />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <section className="inline-flex" data-test-id="event-date">
            <FontAwesomeIcon className="mr-2 mt-1" icon={faCalendar} />
            <p className="text-content-default-content-text font-inter leading-5">
              {getSigningAppointmentDisplayDateTime(appointment)}
            </p>
          </section>
          <section className="inline-flex" data-test-id="event-address">
            <Show when={appointment.signingMethod !== eSigningMethod.eSign}>
              <FontAwesomeIcon className="mr-2 mt-1" icon={faLocationPin} />
              <p className="text-content-default-content-text font-inter leading-5">
                {getSigningAppointmentDisplayAddress(appointment)}
              </p>
            </Show>
          </section>
        </div>

        <div className="flex justify-between">
          <div className="flex items-center gap-6">
            <section className="inline-flex" data-test-id="event-date">
              <FontAwesomeIcon className="mr-2 mt-1" icon={faUsers} />
              <span className="text-content-default-content-text font-inter leading-5">
                {!!signers?.length
                  ? signers.map(({ firstName, lastName }) => `${firstName} ${lastName}`).join(', ')
                  : 'Unassigned'}
              </span>
            </section>
            <section className="inline-flex" data-test-id="event-date">
              <FontAwesomeIcon className="mr-2 mt-1" icon={faStamp} />
              <span className="text-content-default-content-text font-inter leading-5">
                {getNotaryDescription(
                  !!isSignatureSyncAppointment,
                  signingMethod as string,
                  notary as INotaryDetailsData
                )}
              </span>
            </section>
            <Show when={false}>
              <section className="inline-flex" data-test-id="event-date">
                <FontAwesomeIcon className="mr-2 mt-1" icon={faFiles} />
                <span className="text-content-default-content-text font-inter leading-5">{'0 Documents'}</span>
              </section>
              <section className="inline-flex" data-test-id="event-date">
                <FontAwesomeIcon className="mr-2 mt-1" icon={faComments} />
                <span className="text-content-default-content-text font-inter leading-5">
                  {!!notes ? '1 Comment' : '0 Comments'}
                </span>
              </section>
            </Show>
          </div>

          <CorrectionsButton secondary onCorrectionsButtonClick={onCreateCorrectionClick} appointment={appointment} />
        </div>
      </SimpleGrid>
    </div>
  );
};
